import React from "react";
import { fixed, toNum } from "../../../../utils/perfFormat";
import { usePhoneTheme } from "../../useIsPhone";
import { CHART, ROLE } from "./chartTokens";

// Two horizontal bars on one track with a median tick (the team's Four
// Factors: the squad on top in bm-orange, its opponents under it in
// bm-muted, the peer median as an ink tick). Each value is printed in its
// bar (or just past a short bar), the median under the track as text, and
// the aria-label says it all.
//
//   PairedBars({ label, right, bars, median, medianLabel, scale, format, phone })
//
// bars    [{ key, label, value, color }] (two: the squad, then the
//         opponents; colours default to ROLE.self / ROLE.opponent)
// median  the tick's value (null: no tick); medianLabel the printed text
//         ("Peer median 49.0"), default "Median {value}"
// scale   the value that fills the track (the mock's 70 / 30 / 50 / 60);
//         a bar is at least 4% and at most 100% wide, so even a tiny value
//         shows; the median tick sits at its true place, 0 to 100% (a
//         median of 0 at the track's start)
// right   a node at the right of the label row (the "edge" / "deficit" tag)
// format  the printed values (default: one decimal)

const cx = (...parts) => parts.filter(Boolean).join(" ");

const widthOf = (v, scale) => {
  const n = toNum(v);
  if (n === null || !scale) return 0;
  return Math.max(4, Math.min(100, (n / scale) * 100));
};

// Where the median tick sits: the value's true place on the track.
const tickOf = (v, scale) => {
  const n = toNum(v);
  if (n === null || !scale) return 0;
  return Math.max(0, Math.min(100, (n / scale) * 100));
};

export const PairedBars = ({ label, right, bars = [], median = null, medianLabel, scale = 100, format = (v) => fixed(v, 1), phone: phoneProp }) => {
  const themePhone = usePhoneTheme();
  const phone = phoneProp ?? themePhone;
  const [top, bottom] = bars;
  const med = toNum(median);
  const tickText = med === null ? null : medianLabel || `Median ${format(med)}`;
  const summary = [label, ...bars.map((b) => `${b.label} ${format(b.value)}`), tickText].filter(Boolean).join(", ");
  const bar = (b, position, fallback) => {
    if (!b) return null;
    const w = widthOf(b.value, scale);
    const inside = w >= 18;
    return (
      <span key={b.key || position} className={cx("absolute left-0 flex h-3 items-center", position === "top" ? "top-0" : "bottom-0")} style={{ width: "100%" }}>
        <span className="h-3 rounded-[3px]" style={{ width: `${w}%`, background: b.color || fallback }} />
        <span
          className={cx("absolute whitespace-nowrap font-archivo text-[10px] font-bold leading-3", inside ? "pl-[6px] text-white" : "pl-1 text-bm-ink")}
          style={inside ? { left: 0 } : { left: `${w}%` }}
        >
          {format(b.value)}
        </span>
      </span>
    );
  };
  return (
    <div className="flex flex-col gap-[6px]">
      <div className="flex items-baseline justify-between gap-2">
        <span className={cx("min-w-0 break-words text-bm-ink", phone ? "bm-label-m" : "bm-label-l")}>{label}</span>
        {right}
      </div>
      <div role="img" aria-label={summary} className="relative h-[30px]">
        {bar(top, "top", ROLE.self)}
        {bar(bottom, "bottom", ROLE.opponent)}
        {med !== null && (
          <span aria-hidden="true" className="absolute -bottom-[3px] -top-[3px] w-[2px] rounded-full" style={{ left: `${tickOf(med, scale)}%`, background: CHART.ink, opacity: 0.4 }} />
        )}
      </div>
      {tickText && <span className="bm-body-xs text-bm-muted">{tickText}</span>}
    </div>
  );
};

export default PairedBars;
